import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from "sonner"
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Camera, Save, User, Mail, Phone, MapPin, Calendar, Lock } from 'lucide-react';
import { DatePicker } from '@/components/ui/date-picker';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useProfileModal } from '@/contexts/ProfileModalContext';

interface UserProfile {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  bio: string;
  birth_date: string;
  avatar_url?: string;
}

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function ProfileModal() {
  const { isOpen, closeModal } = useProfileModal();
  const { user } = useAuth();
;
  const [isLoading, setIsLoading] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const [profile, setProfile] = useState<UserProfile>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    bio: "",
    birth_date: "",
    avatar_url: "",
  });

  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('first_name, last_name, avatar_url, phone, address, bio, birth_date')
          .eq('id', user.id)
          .single();
        
        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching profile:', error);
          return;
        }
        
        // Sempre atualiza o email do usuário autenticado
        setProfile(prev => ({
          ...prev,
          email: user?.email || "",
          first_name: data?.first_name || "",
          last_name: data?.last_name || "",
          avatar_url: data?.avatar_url || "",
          phone: data?.phone || "",
          address: data?.address || "",
          bio: data?.bio || "",
          birth_date: data?.birth_date || "",
        }));
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    if (isOpen) {
      fetchProfile();
    } else {
      // Limpar campos de senha quando o modal fechar
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  }, [user?.id, user?.email, isOpen]);

  const handleInputChange = (field: keyof UserProfile, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!user?.id) {
      toast.error('Erro de autenticação. Faça login novamente.');
      return;
    }
    
    // Validação dos campos obrigatórios
    if (!profile.first_name.trim()) {
      toast.error('O campo Nome é obrigatório.');
      return;
    }
    
    if (!profile.last_name.trim()) {
      toast.error('O campo Sobrenome é obrigatório.');
      return;
    }
    
    if (!profile.email.trim()) {
      toast.error('O campo Email é obrigatório.');
      return;
    }
    
    setIsLoading(true);
    try {
      const updateData = {
        id: user.id,
        first_name: profile.first_name,
        last_name: profile.last_name,
        avatar_url: profile.avatar_url,
        phone: profile.phone,
        address: profile.address,
        bio: profile.bio,
        birth_date: profile.birth_date && profile.birth_date.trim() !== '' ? profile.birth_date : null,
        updated_at: new Date().toISOString(),
      };
      
      const { error } = await supabase
        .from('profiles')
        .upsert(updateData);
      
      if (error) {
        throw error;
      }
      
      toast.success("Suas informações foram salvas com sucesso.");
      closeModal();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error("Ocorreu um erro ao salvar suas informações.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error("Preencha todos os campos de senha.");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("A nova senha e a confirmação devem ser iguais.");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }

    setIsChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) {
        throw error;
      }

      toast.success("Sua senha foi alterada com sucesso.");

      // Limpar campos de senha
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error: unknown) {
      console.error('Error changing password:', error);
      const errorMessage = error instanceof Error ? error.message : "Ocorreu um erro ao alterar sua senha.";
      toast.error(errorMessage);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handlePasswordInputChange = (field: keyof PasswordData, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleAvatarChange = () => {
    toast.info("Funcionalidade de upload de avatar será implementada em breve.");
  };

  const getInitials = () => {
    const firstName = profile.first_name || user?.email?.split('@')[0] || 'U';
    const lastName = profile.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Editar Perfil
          </DialogTitle>
          <DialogDescription>
            Atualize suas informações pessoais
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Avatar Section */}
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <Avatar className="h-24 w-24">
                <AvatarImage src={profile.avatar_url || ''} alt="Foto do usuário" />
                <AvatarFallback className="bg-primary text-primary-foreground text-base font-semibold">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <Button
                size="icon"
                variant="secondary"
                className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full"
                onClick={handleAvatarChange}
              >
                <Camera className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Separator />

          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <User className="h-5 w-5" />
              Informações Pessoais
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Nome <span className="text-red-500">*</span></Label>
                <Input
                  id="first_name"
                  value={profile.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  placeholder="Seu nome"
                  readOnly
                  onClick={(e) => {
                    e.currentTarget.readOnly = false;
                    e.currentTarget.focus();
                  }}
                  onBlur={(e) => {
                    e.currentTarget.readOnly = true;
                  }}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="last_name">Sobrenome <span className="text-red-500">*</span></Label>
                <Input
                  id="last_name"
                  value={profile.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  placeholder="Seu sobrenome"
                  readOnly
                  onClick={(e) => {
                    e.currentTarget.readOnly = false;
                    e.currentTarget.focus();
                  }}
                  onBlur={(e) => {
                    e.currentTarget.readOnly = true;
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={profile.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="seu@email.com"
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Telefone
              </Label>
              <Input
                id="phone"
                value={profile.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="(11) 99999-9999"
                readOnly
                onClick={(e) => {
                  e.currentTarget.readOnly = false;
                  e.currentTarget.focus();
                }}
                onBlur={(e) => {
                  e.currentTarget.readOnly = true;
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="birth_date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Data de Nascimento
              </Label>
              <DatePicker
                value={profile.birth_date}
                onChange={(date) => handleInputChange('birth_date', date)}
                placeholder="Selecione sua data de nascimento"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Endereço
              </Label>
              <Input
                id="address"
                value={profile.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Seu endereço completo"
                readOnly
                onClick={(e) => {
                  e.currentTarget.readOnly = false;
                  e.currentTarget.focus();
                }}
                onBlur={(e) => {
                  e.currentTarget.readOnly = true;
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Biografia</Label>
              <Textarea
                id="bio"
                value={profile.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                placeholder="Conte um pouco sobre você..."
                rows={3}
                readOnly
                onClick={(e) => {
                  e.currentTarget.readOnly = false;
                  e.currentTarget.focus();
                }}
                onBlur={(e) => {
                  e.currentTarget.readOnly = true;
                }}
              />
            </div>
          </div>

          <Separator />

          {/* Security Section */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Segurança
            </h3>
            
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Senha Atual</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                  placeholder="Digite sua senha atual"
                  readOnly
                  onClick={(e) => {
                    e.currentTarget.readOnly = false;
                    e.currentTarget.focus();
                  }}
                  onBlur={(e) => {
                    e.currentTarget.readOnly = true;
                  }}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new_password">Nova Senha</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                  placeholder="Digite sua nova senha"
                  readOnly
                  onClick={(e) => {
                    e.currentTarget.readOnly = false;
                    e.currentTarget.focus();
                  }}
                  onBlur={(e) => {
                    e.currentTarget.readOnly = true;
                  }}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirmar Nova Senha</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                  placeholder="Confirme sua nova senha"
                  readOnly
                  onClick={(e) => {
                    e.currentTarget.readOnly = false;
                    e.currentTarget.focus();
                  }}
                  onBlur={(e) => {
                    e.currentTarget.readOnly = true;
                  }}
                />
              </div>

              <Button
                onClick={handlePasswordChange}
                disabled={isChangingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                className="w-full sm:w-auto"
                variant="outline"
              >
                {isChangingPassword ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                    Alterando...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Alterar Senha
                  </div>
                )}
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              variant="outline"
              onClick={closeModal}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={isLoading}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Salvando...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Save className="h-4 w-4" />
                  Salvar alterações
                </div>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}